import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import MuiTable, { Padding, Size } from "@material-ui/core/Table";
import classNames from "classnames";
import React from "react";
import {
  TableBodyColumnSchema,
  TableHeaderFooterColumn,
  TablePagingStateProps,
  TableSortingStateProps,
} from "../models";
import { getPagedData, localDataSort } from "../utils";
import { TableBody } from "./TableBody";
import { TableFooter } from "./TableFooter";
import { TableHead } from "./TableHead";

const styles = (theme: Theme) =>
  createStyles({
    tableWrapper: {
      overflowX: "auto",
      borderTop: `solid 1px ${theme.palette.divider}`,
      borderBottom: `solid 1px ${theme.palette.divider}`,
    },
    table: {
      position: "relative",
    },
  });

export interface TableSortingProps extends TableSortingStateProps {
  onSortChange?: (sortPropertyName: string, sortDirection: "asc" | "desc") => void;
}

export interface TablePagingProps extends TablePagingStateProps {
  onPageIndexChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

// onPageIndexChange and onPageSizeChange is defined locally and does not need to be passed in for localPaging...
export interface LocalPagingProps extends TablePagingStateProps {}

interface TableProps extends WithStyles<typeof styles> {
  id?: string;
  className?: string;
  height?: number;
  padding?: Padding;
  size?: Size;
  columnSchemas: TableBodyColumnSchema[];
  data: any[];
  dataSourceType: "local" | "remote";
  sorting?: TableSortingProps;
  paging?: TablePagingProps | LocalPagingProps;
  headerRow?: TableHeaderFooterColumn[];
  footerRow?: TableHeaderFooterColumn[];
  expandableRow?: {
    columnSchemas: TableBodyColumnSchema[];
    keyPropertyName: string;
    dataPropertyName: string;
  };
  isLoading?: boolean;
  loadingText?: string;
}

interface TableState {
  data: any[];
  expandedKeys: string[];
  localDataSourceProps?: {
    sorting?: TableSortingProps;
    paging?: TablePagingProps;
  };
}

export const TableComponent = class extends React.Component<TableProps, TableState> {
  constructor(props: TableProps) {
    super(props);
    const { data, dataSourceType, sorting, paging } = props;

    let sortedAndPagedData = data;
    let localSorting: TableSortingProps | undefined;
    let localPaging: TablePagingProps | undefined;

    if (dataSourceType === "local") {
      if (sorting) {
        localSorting = {
          sortDirection: sorting.sortDirection,
          sortPropertyName: sorting.sortPropertyName,
          onSortChange: this.handleOnLocalSortChange,
        };
        sortedAndPagedData = data.sort(localDataSort(localSorting.sortPropertyName, localSorting.sortDirection));
      }

      if (paging) {
        localPaging = {
          pageIndex: paging.pageIndex,
          pageSize: paging.pageSize,
          totalCount: data.length,
          onPageIndexChange: this.handleOnLocalPageIndexChange,
          onPageSizeChange: this.handleOnLocalPageSizeChange,
        };
        sortedAndPagedData = getPagedData(data, localPaging.pageIndex, localPaging.pageSize);
      }
    }

    this.state = {
      data: sortedAndPagedData,
      localDataSourceProps: localSorting || localPaging ? { sorting: localSorting, paging: localPaging } : undefined,
      expandedKeys: [],
    };
  }

  public render() {
    const {
      id,
      className,
      height,
      padding,
      size,
      columnSchemas,
      sorting,
      paging,
      headerRow,
      footerRow,
      expandableRow,
      isLoading = false,
      loadingText = "Loading Results...",
      classes,
    } = this.props;
    const { data, expandedKeys, localDataSourceProps } = this.state;

    const tableClassName = classNames(classes.tableWrapper, className);

    const sortingProps = localDataSourceProps && localDataSourceProps.sorting ? localDataSourceProps.sorting : sorting;
    const pagingProps =
      localDataSourceProps && localDataSourceProps.paging ? localDataSourceProps.paging : (paging as TablePagingProps);

    return (
      <div id={id ? `${id}__wrapper` : undefined} className={tableClassName} style={{ height }}>
        <MuiTable id={id} padding={padding} size={size} className={classes.table}>
          <TableHead
            id={id ? `${id}__head` : undefined}
            headerRow={headerRow}
            columnSchemas={columnSchemas}
            sorting={sortingProps}
            sticky={!!height}
            isLoading={isLoading}
          />
          <TableBody
            id={id ? `${id}__body` : undefined}
            columnSchemas={columnSchemas}
            data={data}
            expandableRowProps={
              expandableRow && { ...expandableRow, expandedKeys, onExpandedToggle: this.handleExpandedRowKeyChanged }
            }
            isLoading={isLoading}
            loadingText={loadingText}
          />
          <TableFooter
            id={id ? `${id}__footer` : undefined}
            footerRow={footerRow}
            paging={pagingProps}
            sticky={!!height}
          />
        </MuiTable>
      </div>
    );
  }

  public componentDidUpdate(prevProps: TableProps, prevState: TableState) {
    const { data: prevData, sorting: prevSorting, paging: prevPaging, isLoading: prevIsLoading } = prevProps;
    const { id, data, sorting, paging, isLoading } = this.props;
    const { localDataSourceProps } = this.state;

    if ((prevIsLoading && !isLoading) || prevData !== data || prevSorting !== sorting || prevPaging !== paging) {
      const tableWrapper = document.getElementById(`${id}__wrapper`) as HTMLElement;
      const tableHead = document.getElementById(`${id}__head`) as HTMLElement;
      // If paging is in effect, and we just finished loading data, ensure we're scrolled to the top of the table body...
      if (tableWrapper && tableHead) {
        // Ensure the wrapper is scrolled to the top, in case we're using a scrollable table and are currently
        // scrolled down
        tableWrapper.scrollTop = 0;
        // If the window scroll-y position is currently greater than zero, than it's likely the case that we're not
        // using a scrollable table, so scroll the window to the top of the table head...
        if (window.scrollY > 0) {
          const tableHeadTopY = window.scrollY + tableHead.getBoundingClientRect().top - 80;
          window.scrollTo(0, tableHeadTopY);
        }
      }

      let sortedAndPagedData = [...data];
      let localSorting: TableSortingProps | undefined;
      let localPaging: TablePagingProps | undefined;

      if (localDataSourceProps) {
        if (sorting) {
          localSorting =
            prevSorting !== sorting
              ? {
                  sortDirection: sorting.sortDirection,
                  sortPropertyName: sorting.sortPropertyName,
                  onSortChange: this.handleOnLocalSortChange,
                }
              : localDataSourceProps.sorting!;
          sortedAndPagedData = sortedAndPagedData.sort(
            localDataSort(localSorting.sortPropertyName, localSorting.sortDirection),
          );
        }

        if (paging) {
          localPaging =
            prevPaging !== paging
              ? {
                  pageIndex: paging.pageIndex,
                  pageSize: paging.pageSize,
                  totalCount: data.length,
                  onPageIndexChange: this.handleOnLocalPageIndexChange,
                  onPageSizeChange: this.handleOnLocalPageSizeChange,
                }
              : localDataSourceProps.paging!;
          sortedAndPagedData = getPagedData(sortedAndPagedData, localPaging.pageIndex, localPaging.pageSize);
        }
      }

      this.setState({
        ...this.state,
        data: sortedAndPagedData,
        localDataSourceProps:
          localSorting || localPaging
            ? {
                sorting: localSorting,
                paging: localPaging,
              }
            : undefined,
      });
    }
  }

  public handleOnLocalSortChange = (sortPropertyName: string, sortDirection: "asc" | "desc") => {
    const { data } = this.props;
    const { localDataSourceProps } = this.state;

    if (localDataSourceProps == null || localDataSourceProps.sorting == null) {
      throw Error("Sorting state is not defined for Table Sort Handler");
    }

    const localSorting: TableSortingProps = {
      ...localDataSourceProps.sorting,
      sortPropertyName,
      sortDirection,
    };

    let sortedAndPagedData = [...data].sort(localDataSort(localSorting.sortPropertyName, localSorting.sortDirection));

    if (localDataSourceProps.paging) {
      sortedAndPagedData = getPagedData(
        sortedAndPagedData,
        localDataSourceProps.paging.pageIndex,
        localDataSourceProps.paging.pageSize,
      );
    }

    this.setState({
      ...this.state,
      data: sortedAndPagedData,
      localDataSourceProps: {
        ...localDataSourceProps,
        sorting: localSorting,
      },
    });
  };

  public handleOnLocalPageIndexChange = (pageIndex: number) => {
    const { data } = this.props;
    const { localDataSourceProps } = this.state;

    if (localDataSourceProps == null || localDataSourceProps.paging == null) {
      throw Error("Paging state is not defined for Table Paging Handlers");
    }

    const localPaging: TablePagingProps = {
      ...localDataSourceProps.paging,
      pageIndex,
    };

    let sortedAndPagedData = localDataSourceProps.sorting
      ? [...data].sort(
          localDataSort(localDataSourceProps.sorting.sortPropertyName, localDataSourceProps.sorting.sortDirection),
        )
      : data;

    sortedAndPagedData = getPagedData(sortedAndPagedData, localPaging.pageIndex, localPaging.pageSize);

    this.setState({
      ...this.state,
      data: sortedAndPagedData,
      localDataSourceProps: {
        ...localDataSourceProps,
        paging: localPaging,
      },
    });
  };

  public handleOnLocalPageSizeChange = (pageSize: number) => {
    const { data } = this.props;
    const { localDataSourceProps } = this.state;

    if (localDataSourceProps == null || localDataSourceProps.paging == null) {
      throw Error("Paging state is not defined for Table Paging Handlers");
    }

    const localPaging: TablePagingProps = {
      ...localDataSourceProps.paging,
      pageSize,
      pageIndex: 0,
    };

    let sortedAndPagedData = localDataSourceProps.sorting
      ? [...data].sort(
          localDataSort(localDataSourceProps.sorting.sortPropertyName, localDataSourceProps.sorting.sortDirection),
        )
      : data;

    sortedAndPagedData = getPagedData(sortedAndPagedData, localPaging.pageIndex, localPaging.pageSize);

    this.setState({
      ...this.state,
      data: sortedAndPagedData,
      localDataSourceProps: {
        ...localDataSourceProps,
        paging: localPaging,
      },
    });
  };

  public handleExpandedRowKeyChanged = (rowKey: string) => {
    const { expandedKeys } = this.state;

    const rowExpandedKeyIndex = expandedKeys.indexOf(rowKey);
    // If this row is currently collapsed, expand it...
    if (rowExpandedKeyIndex === -1) {
      this.setState({ ...this.state, expandedKeys: [...expandedKeys, rowKey] });
    }
    // Otherwise, this row must be expanded, so collapse it...
    else {
      this.setState({
        ...this.state,
        expandedKeys: [...expandedKeys.slice(0, rowExpandedKeyIndex), ...expandedKeys.slice(rowExpandedKeyIndex + 1)],
      });
    }
  };
};

export const Table = withStyles(styles)(TableComponent);
